/**
 * map-labels.ts — Lógica pura de la capa "Etiquetas" del mapa: qué texto
 * mostrar según el zoom, tamaño estimado y colocación sin encimarse.
 * Sin Leaflet ni DOM para poder probarla directamente.
 */

export interface LabelFields {
  name: boolean;
  crop: boolean;
  hectares: boolean;
  code: boolean;
}

export const DEFAULT_LABEL_FIELDS: LabelFields = { name: true, crop: true, hectares: true, code: false };

export interface LabelSource {
  name?: string;
  code?: string;
  crop?: string;
  hectares?: number;
}

/** 'none' = sin etiquetas (zoom lejano, el mapa muestra clusters). */
export type LabelDetail = 'none' | 'name' | 'full';

export const LABELS_MIN_ZOOM = 12;
export const LABELS_FULL_ZOOM = 14;

export function labelDetailForZoom(zoom: number): LabelDetail {
  if (zoom < LABELS_MIN_ZOOM) return 'none';
  return zoom >= LABELS_FULL_ZOOM ? 'full' : 'name';
}

/** Líneas de texto de una etiqueta. Lejos: solo el nombre (o el código si el nombre está apagado). */
export function buildLabelLines(src: LabelSource, fields: LabelFields, detail: LabelDetail): string[] {
  if (detail === 'none') return [];
  const lines: string[] = [];
  const name = fields.name ? src.name?.trim() : undefined;
  const code = fields.code ? src.code?.trim() : undefined;

  if (detail === 'name') {
    const primary = name || code;
    return primary ? [primary] : [];
  }

  const head = [name, code && code !== name ? (name ? `(${code})` : code) : undefined].filter(Boolean).join(' ');
  if (head) lines.push(head);
  if (fields.crop && src.crop?.trim()) lines.push(src.crop.trim());
  if (fields.hectares && src.hectares != null && Number.isFinite(src.hectares)) {
    lines.push(`${src.hectares.toFixed(2)} ha`);
  }
  return lines;
}

export const LABEL_CHAR_W = 6.5;
export const LABEL_LINE_H = 14;
export const LABEL_PAD = 8;

export function estimateLabelSize(lines: string[]): { w: number; h: number } {
  const longest = lines.reduce((m, l) => Math.max(m, l.length), 0);
  return { w: Math.ceil(longest * LABEL_CHAR_W + LABEL_PAD), h: lines.length * LABEL_LINE_H + LABEL_PAD };
}

export interface LabelCandidate {
  id: string;
  /** Centro en píxeles (x, y) */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Mayor = se coloca antes y gana en colisiones */
  priority: number;
  /** Tamaño en píxeles del polígono que etiqueta; si la etiqueta no cabe se oculta. */
  polyW?: number;
  polyH?: number;
}

export interface PlacedLabel { id: string; x: number; y: number }

function overlaps(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }): boolean {
  return Math.abs(a.x - b.x) * 2 < a.w + b.w && Math.abs(a.y - b.y) * 2 < a.h + b.h;
}

/**
 * Colocación voraz: por prioridad; prueba el centro y luego desplazamientos
 * verticales simples. Si no cabe en su polígono o toda posición choca, se oculta.
 */
type Pt = { x: number; y: number };

function pointInRing(p: Pt, ring: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

function distToRing(p: Pt, ring: Pt[]): number {
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j], b = ring[i];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
    best = Math.min(best, Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy)));
  }
  return best;
}

/**
 * Punto de anclaje de la etiqueta: el más "adentro" del polígono (mayor
 * distancia al borde) que no cae sobre las zonas hijas (`holes`). Evita que el
 * promedio de vértices deje la etiqueta fuera o encima de otra geocerca.
 * Devuelve null si el polígono es degenerado.
 */
export function labelAnchor(ring: Pt[], holes: Pt[][] = [], steps = 28): Pt | null {
  if (ring.length < 3) return null;
  const xs = ring.map(p => p.x), ys = ring.map(p => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  let best: Pt | null = null;
  let bestD = -1;
  for (let i = 0; i <= steps; i++) {
    for (let j = 0; j <= steps; j++) {
      const p = { x: minX + ((maxX - minX) * i) / steps, y: minY + ((maxY - minY) * j) / steps };
      if (!pointInRing(p, ring) || holes.some(h => pointInRing(p, h))) continue;
      const d = Math.min(distToRing(p, ring), ...holes.map(h => distToRing(p, h)));
      if (d > bestD) { bestD = d; best = p; }
    }
  }
  return best;
}

export function placeLabels(candidates: LabelCandidate[]): PlacedLabel[] {
  const sorted = [...candidates].sort((a, b) => b.priority - a.priority);
  const placed: { x: number; y: number; w: number; h: number }[] = [];
  const out: PlacedLabel[] = [];

  for (const c of sorted) {
    if (c.polyW != null && c.polyH != null && (c.w > c.polyW || c.h > c.polyH)) continue;
    const offsets = [0, c.h, -c.h, 2 * c.h, -2 * c.h];
    for (const dy of offsets) {
      const box = { x: c.x, y: c.y + dy, w: c.w, h: c.h };
      if (placed.some(p => overlaps(box, p))) continue;
      placed.push(box);
      out.push({ id: c.id, x: box.x, y: box.y });
      break;
    }
  }
  return out;
}

const STORAGE_KEY = 'agrovid.map.labels';

export interface LabelPrefs { enabled: boolean; fields: LabelFields }

export function loadLabelPrefs(): LabelPrefs {
  const fallback: LabelPrefs = { enabled: false, fields: { ...DEFAULT_LABEL_FIELDS } };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const p = JSON.parse(raw);
    const f = p?.fields ?? {};
    return {
      enabled: p?.enabled === true,
      fields: {
        name: typeof f.name === 'boolean' ? f.name : DEFAULT_LABEL_FIELDS.name,
        crop: typeof f.crop === 'boolean' ? f.crop : DEFAULT_LABEL_FIELDS.crop,
        hectares: typeof f.hectares === 'boolean' ? f.hectares : DEFAULT_LABEL_FIELDS.hectares,
        code: typeof f.code === 'boolean' ? f.code : DEFAULT_LABEL_FIELDS.code,
      },
    };
  } catch {
    return fallback;
  }
}

export function saveLabelPrefs(prefs: LabelPrefs): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* sin storage: no pasa nada */ }
}
